import { useState } from "react";
import {
  ArrowUpRight,
  BookText,
  ChevronDown,
  LogOut,
  Settings,
  SunMoon,
} from "lucide-react";
import { Link, useRouterState } from "@tanstack/react-router";
import type { LinkProps } from "@tanstack/react-router";
import { isMigratedPath } from "@/route-manifest";
import { useTranslation } from "react-i18next";
import { cn } from "@/utils/cn";
import { useAuth } from "@/contexts/AuthContext";
import { useThemeMode } from "@/contexts/ThemeContext";
import { LegacyLink } from "./LegacyLink";
import { SidebarConversations } from "./SidebarConversations";
import { SidebarLogo } from "./SidebarLogo";
import { isNavItemActive, useNavSections } from "./nav-items";
import type { NavItem } from "./nav-items";

const DOCUMENTATION_URL = "https://docs.bunyan.ai";
const NAVIGATION_FOCUS_CLASS =
  "focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-sidebar-ring focus-visible:ring-offset-2 focus-visible:ring-offset-sidebar";

const ProfileAvatar = ({
  displayName,
  photoURL,
}: {
  displayName: string;
  photoURL?: string;
}) => {
  const [failedPhotoURL, setFailedPhotoURL] = useState<string>();
  const showPhoto = Boolean(photoURL && photoURL !== failedPhotoURL);

  return showPhoto ? (
    <img
      data-slot="sidebar-profile-avatar"
      src={photoURL}
      alt={displayName}
      className="size-5 shrink-0 rounded-full object-cover"
      onError={() => setFailedPhotoURL(photoURL)}
    />
  ) : (
    <span
      data-slot="sidebar-profile-avatar"
      className="flex size-5 shrink-0 items-center justify-center rounded-full bg-sidebar-primary text-xs text-sidebar-primary-foreground"
      aria-hidden="true"
    >
      {displayName.charAt(0).toUpperCase()}
    </span>
  );
};

const NavLink = ({
  item,
  collapsed,
  desktopGeometry,
  onNavigate,
}: {
  item: NavItem;
  collapsed: boolean;
  desktopGeometry: boolean;
  onNavigate?: () => void;
}) => {
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const active = isNavItemActive(pathname, item);
  const Icon = item.icon;
  const iconSize = desktopGeometry && collapsed ? "size-5" : "size-4";

  const className = cn(
    "flex items-center rounded-md text-sm leading-[21px] tracking-[0.07px] transition-colors",
    "text-sidebar-foreground/80 hover:bg-sidebar-accent hover:text-sidebar-accent-foreground",
    NAVIGATION_FOCUS_CLASS,
    desktopGeometry
      ? cn(
          "h-8 px-3",
          collapsed ? "w-[36px] gap-3" : "min-w-0 w-full gap-2",
        )
      : "gap-3 px-3 py-2",
    active && "bg-sidebar-primary text-sidebar-primary-foreground",
    collapsed && "justify-center px-2",
  );

  const body = (
    <>
      <Icon className={cn(iconSize, "shrink-0")} aria-hidden="true" />
      {!collapsed && <span className="truncate">{item.label}</span>}
    </>
  );

  const title = collapsed ? item.label : undefined;
  const ariaLabel = collapsed ? item.label : undefined;

  // A migrated screen gets a real typed <Link>, checked against the generated
  // route tree. LegacyLink would still navigate correctly — it pushes the same
  // URL and the router re-matches it — but it would tell us nothing about
  // whether the route exists.
  return isMigratedPath(item.to) ? (
    <Link
      to={item.to as LinkProps["to"]}
      activeOptions={{ exact: item.activeMatch === "exact" }}
      title={title}
      aria-label={ariaLabel}
      aria-current={active ? "page" : undefined}
      className={className}
      onClick={onNavigate}
    >
      {body}
    </Link>
  ) : (
    <LegacyLink
      to={item.to}
      title={title}
      aria-label={ariaLabel}
      {...(active ? { "aria-current": "page" as const } : {})}
      className={className}
      onClick={onNavigate}
    >
      {body}
    </LegacyLink>
  );
};

const SidebarSection = ({
  label,
  collapsible,
  collapsed,
  desktopGeometry,
  desktopContentGap = false,
  children,
}: {
  label?: string;
  collapsible: boolean;
  collapsed: boolean;
  desktopGeometry: boolean;
  desktopContentGap?: boolean;
  children: React.ReactNode;
}) => {
  const [open, setOpen] = useState(true);

  if (!label) {
    return (
      <div className={desktopGeometry ? undefined : "space-y-0.5"}>
        {children}
      </div>
    );
  }

  if (collapsed) {
    return open ? (
      <div className={desktopGeometry ? undefined : "space-y-0.5"}>
        {children}
      </div>
    ) : null;
  }

  return (
    <div className={desktopGeometry ? undefined : "space-y-0.5"}>
      {collapsible ? (
        <button
          type="button"
          onClick={() => setOpen((v) => !v)}
          aria-expanded={open}
          className={cn(
            "flex w-full items-center justify-between rounded-md px-3 text-xs font-medium leading-[18px] tracking-[0.18px] text-sidebar-foreground/60 hover:text-sidebar-foreground",
            NAVIGATION_FOCUS_CLASS,
            desktopGeometry ? "h-8" : "py-1.5",
          )}
        >
          <span>{label}</span>
          <ChevronDown
            className={cn("size-3.5 transition-transform", !open && "-rotate-90")}
            aria-hidden="true"
          />
        </button>
      ) : (
        <div className="px-3 py-1.5 text-xs font-medium leading-[18px] tracking-[0.18px] text-sidebar-foreground/60">
          {label}
        </div>
      )}
      {open && (
        <div
          className={
            desktopGeometry
              ? desktopContentGap
                ? "mt-1"
                : undefined
              : "space-y-0.5"
          }
        >
          {children}
        </div>
      )}
    </div>
  );
};

export const AppSidebar = ({
  collapsed,
  id,
  onNavigate,
  surface = "desktop",
}: {
  collapsed: boolean;
  id?: string;
  onNavigate?: () => void;
  surface?: "desktop" | "mobile";
}) => {
  const { t } = useTranslation();
  const sections = useNavSections();
  const { currentUser, userProfile, logOut } = useAuth();
  const { toggleTheme } = useThemeMode();
  const desktopGeometry = surface === "desktop";
  const desktopItemWidth = collapsed ? "w-[36px]" : "w-[224px]";
  const iconSize = desktopGeometry && collapsed ? "size-5" : "size-4";

  const displayName =
    userProfile?.displayName ||
    userProfile?.name ||
    currentUser?.displayName ||
    userProfile?.username ||
    currentUser?.username ||
    currentUser?.email ||
    "";
  const photoURL =
    (typeof userProfile?.photoURL === "string" && userProfile.photoURL.trim()) ||
    (typeof currentUser?.photoURL === "string" && currentUser.photoURL.trim()) ||
    undefined;

  return (
    <aside
      id={id}
      className={cn(
        "flex h-full shrink-0 flex-col bg-sidebar text-sidebar-foreground transition-[width]",
        desktopGeometry
          ? cn("px-2 py-4", collapsed ? "w-[52px]" : "w-[240px]")
          : collapsed
            ? "w-16"
            : "w-64",
      )}
    >
      <div
        className={cn(
          "flex shrink-0 items-center",
          desktopGeometry
            ? cn("mb-4", collapsed ? "h-[36px] justify-center" : "h-[59px]")
            : "h-16 px-4",
          !desktopGeometry && collapsed && "justify-center px-2",
        )}
      >
        <Link
          to="/"
          className={cn(
            "flex items-center overflow-hidden rounded-md",
            NAVIGATION_FOCUS_CLASS,
            desktopGeometry &&
              (collapsed
                ? "w-full justify-center [&>svg]:h-[27.2px] [&>svg]:w-9"
                : "h-full w-full px-4"),
          )}
          aria-label="Bunyan"
          onClick={onNavigate}
        >
          <SidebarLogo
            collapsed={collapsed}
            desktopGeometry={desktopGeometry}
          />
        </Link>
      </div>

      <nav
        aria-label={t("sidebar.knowledgeBase")}
        className={cn(
          "flex-1 space-y-4 overflow-y-auto",
          !desktopGeometry && "px-2 py-2",
        )}
      >
        {sections.map((section) => (
          <SidebarSection
            key={section.key}
            label={section.label}
            collapsible={section.collapsible}
            collapsed={collapsed}
            desktopGeometry={desktopGeometry}
          >
            {section.items.map((item) => (
              <NavLink
                key={item.key}
                item={item}
                collapsed={collapsed}
                desktopGeometry={desktopGeometry}
                onNavigate={onNavigate}
              />
            ))}
          </SidebarSection>
        ))}

        {/*
          Conversations are a live list, not navigation config, so they are not
          in nav-items.ts — that file is the permission contract carried over
          from DashboardLayout and is worth keeping declarative.
        */}
        <SidebarSection
          label={t("conversation.conversations")}
          collapsible
          collapsed={collapsed}
          desktopGeometry={desktopGeometry}
          desktopContentGap
        >
          <SidebarConversations
            collapsed={collapsed}
            desktopGeometry={desktopGeometry}
            onNavigate={onNavigate}
          />
        </SidebarSection>
      </nav>

      <div
        className={cn(
          "shrink-0",
          !desktopGeometry && "p-2",
        )}
      >
        <button
          type="button"
          onClick={toggleTheme}
          aria-label={collapsed ? t("sidebar.darkTheme") : undefined}
          title={collapsed ? t("sidebar.darkTheme") : undefined}
          className={cn(
            "flex w-full items-center gap-3 rounded-md text-sm leading-[21px] tracking-[0.07px] text-sidebar-foreground/80 hover:bg-sidebar-accent hover:text-sidebar-accent-foreground",
            desktopGeometry
              ? cn("h-8 px-3", desktopItemWidth)
              : "px-3 py-2",
            NAVIGATION_FOCUS_CLASS,
            collapsed && "justify-center px-2",
          )}
        >
          <SunMoon className={cn(iconSize, "shrink-0")} aria-hidden="true" />
          {!collapsed && <span>{t("sidebar.darkTheme")}</span>}
        </button>

        <LegacyLink
          to="/settings"
          onClick={onNavigate}
          aria-label={collapsed ? t("sidebar.settings") : undefined}
          title={collapsed ? t("sidebar.settings") : undefined}
          className={cn(
            "flex items-center gap-3 rounded-md text-sm leading-[21px] tracking-[0.07px] text-sidebar-foreground/80 hover:bg-sidebar-accent hover:text-sidebar-accent-foreground",
            desktopGeometry
              ? cn("h-8 px-3", desktopItemWidth)
              : "px-3 py-2",
            NAVIGATION_FOCUS_CLASS,
            collapsed && "justify-center px-2",
          )}
        >
          <Settings className={cn(iconSize, "shrink-0")} aria-hidden="true" />
          {!collapsed && <span>{t("sidebar.settings")}</span>}
        </LegacyLink>

        <a
          href={DOCUMENTATION_URL}
          target="_blank"
          rel="noreferrer"
          onClick={onNavigate}
          aria-label={collapsed ? t("sidebar.documentation") : undefined}
          title={collapsed ? t("sidebar.documentation") : undefined}
          className={cn(
            "flex items-center gap-3 rounded-md text-sm leading-[21px] tracking-[0.07px] text-sidebar-foreground/80 hover:bg-sidebar-accent hover:text-sidebar-accent-foreground",
            desktopGeometry
              ? cn("h-8 px-3", desktopItemWidth)
              : "px-3 py-2",
            NAVIGATION_FOCUS_CLASS,
            collapsed && "justify-center px-2",
          )}
        >
          <BookText className={cn(iconSize, "shrink-0")} aria-hidden="true" />
          {!collapsed && (
            <>
              <span className="flex-1">{t("sidebar.documentation")}</span>
              <ArrowUpRight className="size-4 shrink-0" aria-hidden="true" />
            </>
          )}
        </a>

        {displayName && (
          <div
            role="group"
            aria-label={collapsed ? displayName : undefined}
            title={collapsed ? displayName : undefined}
            className={cn(
              "flex items-center gap-3 rounded-md",
              desktopGeometry
                ? cn("h-9 px-3", desktopItemWidth)
                : "px-3 py-2",
              collapsed && "justify-center px-2",
            )}
          >
            <ProfileAvatar displayName={displayName} photoURL={photoURL} />
            {!collapsed && (
              <>
                <span className="min-w-0 flex-1 truncate text-sm leading-[21px] tracking-[0.07px]">
                  {displayName}
                </span>
                <button
                  type="button"
                  onClick={logOut}
                  aria-label={t("common.logout")}
                  className={cn(
                    "rounded p-1 text-sidebar-foreground/70 hover:bg-sidebar-accent hover:text-sidebar-accent-foreground",
                    NAVIGATION_FOCUS_CLASS,
                  )}
                >
                  <LogOut className="size-4" aria-hidden="true" />
                </button>
              </>
            )}
          </div>
        )}
      </div>
    </aside>
  );
};
